import * as React from 'react';

import {
  enumInventoryZoneSortOption,
  enumZoneStatus,
  enumZoneType,
  InventoryZoneSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  DataGrid,
  Pagination,
  type PaginationProps,
  SearchField,
  Select,
  Button,
  ListScreenLayout,
  type ListStatsCardProps,
  DialogOverlay,
  DialogContent,
  DialogHeader,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideMapPin,
  LucideCheckCircle,
  LucideXCircle,
  LucideArchive,
  LucidePlus,
  LucideAlertTriangle,
} from 'lucide-react';

import {
  closeInventoryZoneAtom,
  archiveInventoryZoneAtom,
} from '../../_graphql/mutationInventoryZone';
import { $remoteFacilities } from '../../_state/facilities.state';
import * as state from '../../_state/inventoryZonesList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InventoryZoneRecord } from '../../types/inventoryZone';

import { InventoryZoneDialog } from './InventoryZoneDialog';
import { useInventoryZonesColumns } from './useInventoryZonesColumns';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: InventoryZoneSortOption;
    desc: InventoryZoneSortOption;
  }
> = {
  zoneCode: {
    asc: enumInventoryZoneSortOption.ZoneCodeAsc,
    desc: enumInventoryZoneSortOption.ZoneCodeDesc,
  },
  zoneType: {
    asc: enumInventoryZoneSortOption.ZoneTypeAsc,
    desc: enumInventoryZoneSortOption.ZoneTypeDesc,
  },
  createdAt: {
    asc: enumInventoryZoneSortOption.CreatedAtAsc,
    desc: enumInventoryZoneSortOption.CreatedAtDesc,
  },
};

export const InventoryZonesListView = () => {
  const t = useTranslations();
  const toast = useToast();

  const [searchTerm, setSearchTerm] = useAtom(state.$searchTerm);
  const [selectedFacility, setSelectedFacility] = useAtom(
    state.$selectedFacility,
  );
  const [selectedZoneType, setSelectedZoneType] = useAtom(
    state.$selectedZoneType,
  );
  const [selectedStatus, setSelectedStatus] = useAtom(state.$selectedStatus);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );
  const [editingZone, setEditingZone] =
    React.useState<InventoryZoneRecord | null>(null);
  const [closingZone, setClosingZone] =
    React.useState<InventoryZoneRecord | null>(null);
  const [archivingZone, setArchivingZone] =
    React.useState<InventoryZoneRecord | null>(null);

  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteInventoryZones,
  );

  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );

  const closeZone = useSetAtom(closeInventoryZoneAtom({}));
  const archiveZone = useSetAtom(archiveInventoryZoneAtom({}));

  const zones = data?.zones ?? [];
  const stats = data?.stats ?? {
    totalZones: 0,
    activeZones: 0,
    closedZones: 0,
    archivedZones: 0,
  };
  const total = data?.totalCount ?? 0;

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumInventoryZoneSortOption.ZoneCodeAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleCreateClick = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  const handleDialogClose = React.useCallback(() => {
    setIsCreateDialogOpen(false);
    setEditingZone(null);
  }, [setIsCreateDialogOpen]);

  const handleDialogSuccess = React.useCallback(() => {
    refetch();
    handleDialogClose();
  }, [refetch, handleDialogClose]);

  const handleEdit = React.useCallback((zone: InventoryZoneRecord) => {
    setEditingZone(zone);
  }, []);

  const handleClose = React.useCallback((zone: InventoryZoneRecord) => {
    setClosingZone(zone);
  }, []);

  const handleArchive = React.useCallback((zone: InventoryZoneRecord) => {
    setArchivingZone(zone);
  }, []);

  const handleConfirmClose = React.useCallback(async () => {
    if (!closingZone) return;
    try {
      await closeZone({ id: closingZone.id });
      toast.success(t.zonesListView.actions.closeSuccess);
      refetch();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t.zonesListView.dialog.unknownError;
      toast.error(t.zonesListView.actions.closeError(errorMessage));
    } finally {
      setClosingZone(null);
    }
  }, [closingZone, closeZone, toast, t, refetch]);

  const handleConfirmArchive = React.useCallback(async () => {
    if (!archivingZone) return;
    try {
      await archiveZone({ id: archivingZone.id });
      toast.success(t.zonesListView.actions.archiveSuccess);
      refetch();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t.zonesListView.dialog.unknownError;
      toast.error(t.zonesListView.actions.archiveError(errorMessage));
    } finally {
      setArchivingZone(null);
    }
  }, [archivingZone, archiveZone, toast, t, refetch]);

  const columns = useInventoryZonesColumns({
    onEdit: handleEdit,
    onClose: handleClose,
    onArchive: handleArchive,
  });

  const facilityOptions = React.useMemo(
    () => [
      { key: 'all', label: t.zonesListView.filters.allFacilities },
      ...facilities.map((f) => ({ key: f.id, label: f.name })),
    ],
    [facilities, t],
  );

  const zoneTypeOptions = React.useMemo(
    () => [
      { key: 'all', label: t.zonesListView.filters.allTypes },
      {
        key: enumZoneType.Zone,
        label: t.zonesListView.zoneTypes.Zone,
      },
      {
        key: enumZoneType.Aisle,
        label: t.zonesListView.zoneTypes.Aisle,
      },
      {
        key: enumZoneType.Shelf,
        label: t.zonesListView.zoneTypes.Shelf,
      },
      {
        key: enumZoneType.Bin,
        label: t.zonesListView.zoneTypes.Bin,
      },
      {
        key: enumZoneType.Other,
        label: t.zonesListView.zoneTypes.Other,
      },
    ],
    [t],
  );

  const statusOptions = React.useMemo(
    () => [
      { key: 'all', label: t.zonesListView.filters.allStatuses },
      {
        key: enumZoneStatus.Active,
        label: t.zonesListView.statusTypes.Active,
      },
      {
        key: enumZoneStatus.Closed,
        label: t.zonesListView.statusTypes.Closed,
      },
      {
        key: enumZoneStatus.Archived,
        label: t.zonesListView.statusTypes.Archived,
      },
    ],
    [t],
  );

  const statsColumns = React.useMemo(
    (): ListStatsCardProps['stats'] => [
      {
        key: 'totalZones',
        label: t.zonesListView.summary.totalZones,
        value: stats.totalZones,
        icon: <LucideMapPin />,
        color: 'primary',
      },
      {
        key: 'activeZones',
        label: t.zonesListView.summary.activeZones,
        value: stats.activeZones,
        icon: <LucideCheckCircle />,
        color: 'success',
      },
      {
        key: 'closedZones',
        label: t.zonesListView.summary.closedZones,
        value: stats.closedZones,
        icon: <LucideXCircle />,
        color: 'warning',
      },
      {
        key: 'archivedZones',
        label: t.zonesListView.summary.archivedZones,
        value: stats.archivedZones,
        icon: <LucideArchive />,
        color: 'failure',
      },
    ],
    [stats, t],
  );

  const emptyPanelProps = {
    title: t.zonesListView.emptyState.title,
    description: <span>{t.zonesListView.emptyState.subtitle}</span>,
    centered: true,
  };

  return (
    <div className="@InventoryZonesListView flex h-full flex-col">
      <ListScreenLayout
        filters={
          <>
            <SearchField
              autoFocus
              value={searchTerm ?? ''}
              onChange={(value) => {
                setSearchTerm(value || undefined);
                setOffset(0);
              }}
              placeholder={t.zonesListView.filters.search}
              className="w-64"
            />
            <Select
              value={selectedFacility || 'all'}
              onChange={(key) => {
                setSelectedFacility(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={facilityOptions}
              className="w-48"
            />
            <Select
              value={selectedZoneType || 'all'}
              onChange={(key) => {
                setSelectedZoneType(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={zoneTypeOptions}
              className="w-36"
            />
            <Select
              value={selectedStatus || 'all'}
              onChange={(key) => {
                setSelectedStatus(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={statusOptions}
              className="w-36"
            />
          </>
        }
        actions={
          <Button
            icon={<LucidePlus />}
            color="primary"
            onPress={handleCreateClick}
          >
            {t.zonesListView.createZone}
          </Button>
        }
        stats={statsColumns}
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<InventoryZoneRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={zones}
          rowKey="id"
          aria-label={t.zonesListView.table.aria}
          onColumnSort={handleColumnSort}
          className="w-full text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.zonesListView.content.error(error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <InventoryZoneDialog
        isOpen={isCreateDialogOpen || !!editingZone}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        editingZone={editingZone}
      />

      {closingZone && (
        <DialogOverlay isOpen className="@CloseZoneDialog">
          <DialogContent>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <LucideAlertTriangle className="size-5 text-warning" />
                <span className="text-base font-semibold">
                  {t.zonesListView.confirmClose.title}
                </span>
              </div>
            </DialogHeader>
            <div className="flex flex-col gap-2 px-4">
              <div className="text-base text-neutral/50">
                {t.zonesListView.confirmClose.description(closingZone.zoneCode)}
              </div>
              <div className="flex justify-end gap-2 py-4">
                <Button
                  variant="text"
                  color="neutral"
                  onPress={() => setClosingZone(null)}
                >
                  {t.zonesListView.confirmClose.cancel}
                </Button>
                <Button
                  variant="solid"
                  color="warning"
                  onPress={handleConfirmClose}
                  icon={<LucideXCircle className="size-4" />}
                >
                  {t.zonesListView.confirmClose.confirm}
                </Button>
              </div>
            </div>
          </DialogContent>
        </DialogOverlay>
      )}

      {archivingZone && (
        <DialogOverlay isOpen className="@ArchiveZoneDialog">
          <DialogContent>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <LucideAlertTriangle className="size-5 text-failure" />
                <span className="text-base font-semibold">
                  {t.zonesListView.confirmArchive.title}
                </span>
              </div>
            </DialogHeader>
            <div className="flex flex-col gap-2 px-4">
              <div className="text-base text-neutral/50">
                {t.zonesListView.confirmArchive.description(
                  archivingZone.zoneCode,
                )}
              </div>
              <div className="flex justify-end gap-2 py-4">
                <Button
                  variant="text"
                  color="neutral"
                  onPress={() => setArchivingZone(null)}
                >
                  {t.zonesListView.confirmArchive.cancel}
                </Button>
                <Button
                  variant="solid"
                  color="failure"
                  onPress={handleConfirmArchive}
                  icon={<LucideArchive className="size-4" />}
                >
                  {t.zonesListView.confirmArchive.confirm}
                </Button>
              </div>
            </div>
          </DialogContent>
        </DialogOverlay>
      )}
    </div>
  );
};
